'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { Plus, Calculator, FileText, CheckCircle, XCircle, Calendar, Search, Filter, X, ChevronRight, Clock, Eye } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

const formatVT = (n: number) => `VT ${n.toLocaleString('en-US')}`;

type LoanStatus = 'Active' | 'Paid' | 'Overdue' | 'Defaulted' | 'Written Off';
type UITab = 'Active' | 'Overdue' | 'Paid' | 'Defaulted';

interface Borrower {
  id: string;
  full_name: string;
}

interface Loan {
  id: string;
  loan_number: string;
  borrower_id: string;
  borrower_name?: string;
  principal_amount: number;
  interest_rate: number;
  loan_term_days: number;
  loan_date: string;
  due_date: string;
  interest_amount: number;
  total_repayment_amount: number;
  amount_paid: number;
  remaining_balance: number;
  status: LoanStatus;
  notes: string;
}

const statusConfig: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  Active: { bg: 'bg-positive/10', text: 'text-positive', icon: <CheckCircle size={12} /> },
  Paid: { bg: 'bg-info/10', text: 'text-info', icon: <CheckCircle size={12} /> },
  Overdue: { bg: 'bg-warning/10', text: 'text-warning', icon: <Clock size={12} /> },
  Defaulted: { bg: 'bg-negative/10', text: 'text-negative', icon: <XCircle size={12} /> },
  'Written Off': { bg: 'bg-negative/10', text: 'text-negative', icon: <XCircle size={12} /> },
};

const TABS: UITab[] = ['Active', 'Overdue', 'Paid', 'Defaulted'];

export default function LoansPage() {
  const [tab, setTab] = useState<UITab>('Active');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<'create' | 'schedule' | 'calc' | null>(null);
  const [selected, setSelected] = useState<Loan | null>(null);
  const [wizardStep, setWizardStep] = useState(1);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [borrowers, setBorrowers] = useState<Borrower[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loanForm, setLoanForm] = useState({ borrowerId: '', purpose: '', principal: '', rate: '20', term: '30', startDate: new Date().toISOString().split('T')[0] });
  const [calcResult, setCalcResult] = useState<{ interest: number; total: number } | null>(null);

  const supabase = createClient();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [loanResponse, borrowerResponse] = await Promise.all([
      supabase.from('loans').select('*, borrowers(full_name)').order('created_at', { ascending: false }),
      supabase.from('borrowers').select('id, full_name').order('full_name'),
    ]);
    const loanData = loanResponse.data;
    const borrowerData = borrowerResponse.data;
    const mapped = (loanData || []).map((l: any) => ({
      ...l,
      borrower_name: l.borrowers?.full_name || '—',
    }));
    setLoans(mapped);
    setBorrowers(borrowerData || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = loans.filter(l => {
    const matchTab = tab === 'Active' ? l.status === 'Active'
      : tab === 'Overdue' ? l.status === 'Overdue'
      : tab === 'Paid' ? l.status === 'Paid'
      : l.status === 'Defaulted' || l.status === 'Written Off';
    const matchSearch = (l.borrower_name || '').toLowerCase().includes(search.toLowerCase()) || l.loan_number?.includes(search);
    return matchTab && matchSearch;
  });

  const tabCounts = TABS.reduce((acc, t) => {
    acc[t] = loans.filter(l =>
      t === 'Active' ? l.status === 'Active'
      : t === 'Overdue' ? l.status === 'Overdue'
      : t === 'Paid' ? l.status === 'Paid'
      : l.status === 'Defaulted' || l.status === 'Written Off'
    ).length;
    return acc;
  }, {} as Record<UITab, number>);

  const calculateInterest = () => {
    const p = parseFloat(loanForm.principal) || 0;
    const r = parseFloat(loanForm.rate) || 20;
    const interest = p * (r / 100);
    setCalcResult({ interest, total: p + interest });
  };

  const openSchedule = (loan: Loan) => { setSelected(loan); setModal('schedule'); };

  const generateSchedule = (loan: Loan) => {
    const weekly = Math.ceil(loan.loan_term_days / 7);
    const perWeek = Math.round(loan.total_repayment_amount / weekly);
    return Array.from({ length: weekly }, (_, i) => ({
      week: i + 1,
      dueDate: new Date(new Date(loan.loan_date).getTime() + (i + 1) * 7 * 86400000).toISOString().split('T')[0],
      amount: perWeek,
      status: loan.amount_paid >= perWeek * (i + 1) ? 'Paid' : i === 0 && loan.amount_paid > 0 ? 'Partial' : 'Due',
    }));
  };

  const handleSubmitLoan = async () => {
    if (!loanForm.borrowerId) { setError('Please select a borrower'); return; }
    if (!loanForm.principal || Number(loanForm.principal) <= 0) { setError('Principal amount must be greater than 0'); return; }
    setSaving(true);
    setError(null);
    const principal = Number(loanForm.principal);
    const rate = Number(loanForm.rate);
    const termDays = Number(loanForm.term);
    const interest = principal * (rate / 100);
    const total = principal + interest;
    const startDate = new Date(loanForm.startDate);
    const dueDate = new Date(startDate.getTime() + termDays * 86400000).toISOString().split('T')[0];
    const loanNumber = `VL-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`;

    const { error: insertError } = await supabase
      .from('loans')
      .insert({
        loan_number: loanNumber,
        borrower_id: loanForm.borrowerId,
        principal_amount: principal,
        interest_rate: rate,
        loan_term_days: termDays,
        loan_date: loanForm.startDate,
        due_date: dueDate,
        interest_amount: interest,
        total_repayment_amount: total,
        remaining_balance: total,
        amount_paid: 0,
        status: 'Active',
        notes: loanForm.purpose,
      });
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
    } else {
      setModal(null);
      setLoanForm({ borrowerId: '', purpose: '', principal: '', rate: '20', term: '30', startDate: new Date().toISOString().split('T')[0] });
      setWizardStep(1);
      fetchData();
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Loans</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Loan lifecycle management — pipeline, contracts, schedules</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => { setWizardStep(1); setError(null); setLoanForm({ borrowerId: '', purpose: '', principal: '', rate: '20', term: '30', startDate: new Date().toISOString().split('T')[0] }); setModal('create'); }} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
              <Plus size={16} /> Create New Loan
            </button>
            <button onClick={() => setModal('calc')} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <Calculator size={16} /> Calculate Interest
            </button>
          </div>
        </div>

        {/* Pipeline Tabs */}
        <div className="flex items-center gap-1 p-1 rounded-xl bg-card border border-border w-fit">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
              {t}
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${tab === t ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>{tabCounts[t]}</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search loans..." className="w-full pl-9 pr-4 py-2 rounded-lg bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">
            <Filter size={15} /> Filter
          </button>
        </div>

        {/* Loans Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Loan ID</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Borrower</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Principal</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Rate / Term</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Due</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Remaining</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Due Date</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground text-sm">Loading loans...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground text-sm">No {tab.toLowerCase()} loans found</td></tr>
                ) : filtered.map(loan => {
                  const cfg = statusConfig[loan.status] || statusConfig['Active'];
                  const progress = loan.total_repayment_amount > 0 ? (loan.amount_paid / loan.total_repayment_amount) * 100 : 0;
                  return (
                    <tr key={loan.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-primary font-semibold">{loan.loan_number}</td>
                      <td className="px-4 py-3 font-medium text-foreground">{loan.borrower_name}</td>
                      <td className="px-4 py-3 text-right font-mono text-foreground">{formatVT(Number(loan.principal_amount))}</td>
                      <td className="px-4 py-3 text-center text-muted-foreground">{loan.interest_rate}% / {loan.loan_term_days}d</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-foreground">{formatVT(Number(loan.total_repayment_amount))}</td>
                      <td className="px-4 py-3 text-right">
                        <div>
                          <span className="font-mono text-sm font-semibold text-foreground">{formatVT(Number(loan.remaining_balance))}</span>
                          <div className="w-full h-1 rounded-full bg-muted mt-1 overflow-hidden">
                            <div className="h-full rounded-full bg-positive" style={{ width: `${progress}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-muted-foreground text-xs">{loan.due_date}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
                          {cfg.icon} {loan.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openSchedule(loan)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="View Payment Schedule">
                            <Calendar size={14} />
                          </button>
                          <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Generate Contract">
                            <FileText size={14} />
                          </button>
                          <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="View Details">
                            <Eye size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modals */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
              <h3 className="font-bold text-foreground">
                {modal === 'create' && `Create New Loan — Step ${wizardStep} of 3`}
                {modal === 'schedule' && `Payment Schedule — ${selected?.loan_number}`}
                {modal === 'calc' && 'Interest Calculator'}
              </h3>
              <button onClick={() => setModal(null)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><X size={16} /></button>
            </div>

            {modal === 'create' && (
              <div className="p-6">
                {/* Step indicators */}
                <div className="flex items-center gap-2 mb-6">
                  {[1, 2, 3].map(s => (
                    <React.Fragment key={s}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${wizardStep >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>{s}</div>
                      {s < 3 && <div className={`flex-1 h-0.5 rounded-full transition-colors ${wizardStep > s ? 'bg-primary' : 'bg-muted'}`} />}
                    </React.Fragment>
                  ))}
                </div>

                {error && <div className="mb-4 p-3 rounded-lg bg-negative/10 border border-negative/20 text-negative text-sm">{error}</div>}

                {wizardStep === 1 && (
                  <div className="space-y-4">
                    <p className="text-sm font-semibold text-foreground mb-3">Borrower & Purpose</p>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Select Borrower *</label>
                      <select value={loanForm.borrowerId} onChange={e => setLoanForm(f => ({ ...f, borrowerId: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                        <option value="">Choose borrower...</option>
                        {borrowers.map(b => <option key={b.id} value={b.id}>{b.full_name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Loan Purpose</label>
                      <input value={loanForm.purpose} onChange={e => setLoanForm(f => ({ ...f, purpose: e.target.value }))} placeholder="e.g. Business stock, School fees" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Start Date</label>
                      <input value={loanForm.startDate} onChange={e => setLoanForm(f => ({ ...f, startDate: e.target.value }))} type="date" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                  </div>
                )}

                {wizardStep === 2 && (
                  <div className="space-y-4">
                    <p className="text-sm font-semibold text-foreground mb-3">Loan Terms</p>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Principal Amount (VT) *</label>
                      <input value={loanForm.principal} onChange={e => setLoanForm(f => ({ ...f, principal: e.target.value }))} placeholder="e.g. 200000" type="number" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Interest Rate (%)</label>
                      <input value={loanForm.rate} onChange={e => setLoanForm(f => ({ ...f, rate: e.target.value }))} type="number" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Term Length (days)</label>
                      <div className="flex gap-2">
                        {[14, 21, 30].map(d => (
                          <button key={d} onClick={() => setLoanForm(f => ({ ...f, term: d.toString() }))} className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${loanForm.term === d.toString() ? 'bg-primary/10 border-primary text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>{d} days</button>
                        ))}
                      </div>
                    </div>
                    {loanForm.principal && (
                      <div className="p-3 rounded-lg bg-positive/5 border border-positive/20">
                        <p className="text-xs text-muted-foreground mb-1">Calculated Summary</p>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Interest ({loanForm.rate}%)</span>
                          <span className="font-mono font-semibold text-positive">+{formatVT(parseFloat(loanForm.principal) * parseFloat(loanForm.rate) / 100)}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-1">
                          <span className="font-semibold text-foreground">Total Due</span>
                          <span className="font-mono font-bold text-foreground">{formatVT(parseFloat(loanForm.principal) * (1 + parseFloat(loanForm.rate) / 100))}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {wizardStep === 3 && (
                  <div className="space-y-4">
                    <p className="text-sm font-semibold text-foreground mb-3">Review & Submit</p>
                    <div className="space-y-2 p-4 rounded-xl bg-muted/30">
                      {[
                        ['Borrower', borrowers.find(b => b.id === loanForm.borrowerId)?.full_name || '—'],
                        ['Purpose', loanForm.purpose || '—'],
                        ['Principal', loanForm.principal ? formatVT(parseFloat(loanForm.principal)) : '—'],
                        ['Interest Rate', `${loanForm.rate}% (flat)`],
                        ['Term', `${loanForm.term} days`],
                        ['Start Date', loanForm.startDate],
                        ['Total Due', loanForm.principal ? formatVT(parseFloat(loanForm.principal) * (1 + parseFloat(loanForm.rate) / 100)) : '—'],
                      ].map(([k, v]) => (
                        <div key={k} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{k}</span>
                          <span className="font-medium text-foreground">{v}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
                        <FileText size={14} /> Generate Contract
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  {wizardStep > 1 && <button onClick={() => setWizardStep(s => s - 1)} className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors">Back</button>}
                  <button
                    onClick={() => wizardStep < 3 ? setWizardStep(s => s + 1) : handleSubmitLoan()}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
                  >
                    {saving ? 'Saving...' : wizardStep < 3 ? (<>Next <ChevronRight size={15} /></>) : (<><CheckCircle size={15} /> Submit Loan</>)}
                  </button>
                </div>
              </div>
            )}

            {modal === 'schedule' && selected && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground">Principal</p>
                    <p className="font-mono font-bold text-foreground text-sm">{formatVT(Number(selected.principal_amount))}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground">Total Due</p>
                    <p className="font-mono font-bold text-foreground text-sm">{formatVT(Number(selected.total_repayment_amount))}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground">Remaining</p>
                    <p className="font-mono font-bold text-negative text-sm">{formatVT(Number(selected.remaining_balance))}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {generateSchedule(selected).map(row => (
                    <div key={row.week} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div>
                        <p className="text-sm font-medium text-foreground">Week {row.week}</p>
                        <p className="text-xs text-muted-foreground">Due: {row.dueDate}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-semibold text-foreground text-sm">{formatVT(row.amount)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${row.status === 'Paid' ? 'bg-positive/10 text-positive' : row.status === 'Partial' ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'}`}>{row.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={() => setModal(null)} className="w-full px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors">Close</button>
              </div>
            )}

            {modal === 'calc' && (
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Principal Amount (VT)</label>
                  <input value={loanForm.principal} onChange={e => setLoanForm(f => ({ ...f, principal: e.target.value }))} placeholder="e.g. 200000" type="number" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Interest Rate (%)</label>
                  <input value={loanForm.rate} onChange={e => setLoanForm(f => ({ ...f, rate: e.target.value }))} type="number" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                </div>
                <button onClick={calculateInterest} className="w-full px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2">
                  <Calculator size={15} /> Calculate
                </button>
                {calcResult && (
                  <div className="p-4 rounded-xl bg-positive/5 border border-positive/20 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Principal</span>
                      <span className="font-mono font-semibold text-foreground">{formatVT(parseFloat(loanForm.principal))}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Interest ({loanForm.rate}%)</span>
                      <span className="font-mono font-semibold text-positive">+{formatVT(calcResult.interest)}</span>
                    </div>
                    <div className="border-t border-border pt-2 flex justify-between">
                      <span className="font-bold text-foreground">Total Due</span>
                      <span className="font-mono font-bold text-foreground text-lg">{formatVT(calcResult.total)}</span>
                    </div>
                  </div>
                )}
                <button onClick={() => setModal(null)} className="w-full px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors">Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
